import React from 'react';
import { View, TouchableOpacity, StyleSheet, Dimensions } from 'react-native';
import PropTypes from 'prop-types';
import { findAll } from 'highlight-words-core';
import Ionicons from '@expo/vector-icons/Ionicons';

import Colors from '../../constants/Colors';
import PantryPhoto from './PantryPhoto';
import { formatPantryAddress, getPantryName, getPhotoUrl } from '../../utils/pantryUtils';

import { AppText } from '../AppText';

/**
 * SearchResultItem - Displays a single search result with highlighted search terms
 * Matches the style of PantryListItem for consistency
 */
function SearchResultItem({ pantry, searchQuery, onPress, translation }) {
  // Split search query into individual words for highlighting
  const searchWords = searchQuery ? searchQuery.split(' ').filter((w) => w.length > 0) : [];

  // Helper function to render text with highlights
  const renderHighlightedText = (text, highlightWords, style) => {
    if (!text || highlightWords.length === 0) {
      return <AppText style={style}>{text}</AppText>;
    }

    // Use highlight-words-core to find matching chunks
    const chunks = findAll({
      searchWords: highlightWords,
      textToHighlight: text,
      autoEscape: true,
    });

    return (
      <AppText style={style}>
        {chunks.map((chunk, index) => {
          const { end, highlight, start } = chunk;
          const chunkText = text.substring(start, end);

          if (highlight) {
            return (
              <AppText key={index} style={styles.highlighted} testID="highlighted-text">
                {chunkText}
              </AppText>
            );
          } else {
            return <AppText key={index}>{chunkText}</AppText>;
          }
        })}
      </AppText>
    );
  };

  // Format distance similar to explore mode
  const formatDistance = (distanceInMiles) => {
    if (!distanceInMiles) return null;
    return `${distanceInMiles.toFixed(1)} mi away`;
  };

  // Build address with proper formatting using utility function
  const { street, cityLine } = formatPantryAddress(pantry);
  const pantryName = getPantryName(pantry);
  const photoUrl = getPhotoUrl(pantry);

  // Get programs label
  const programsLabels = [];
  if (pantry.programs && pantry.programs.length) {
    pantry.programs.forEach((program) => {
      const programLabel = program.toLowerCase().replace(/ /g, '_');
      if (translation && translation[`program_type_${programLabel}`]) {
        programsLabels.push(translation[`program_type_${programLabel}`]);
      }
    });
  }
  const programsLabel = programsLabels.sort().join(', ');

  return (
    <TouchableOpacity
      style={styles.container}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${pantryName}${pantry.distance ? `, ${formatDistance(pantry.distance)}` : ''}`}
      testID={`search-result-item-${pantry.id}`}
    >
      <PantryPhoto url={photoUrl} />
      <View style={styles.pantryDetailsTextContainer}>
        {renderHighlightedText(pantryName, searchWords, styles.pantryDetailsName)}

        {programsLabel ? <AppText style={styles.programsLabel}>{programsLabel}</AppText> : null}

        {street || cityLine ? (
          <View style={styles.addressContainer}>
            {street ? <View>{renderHighlightedText(street, searchWords, styles.addressText)}</View> : null}
            {cityLine ? (
              <View>{renderHighlightedText(cityLine, searchWords, styles.addressText)}</View>
            ) : null}
          </View>
        ) : null}

        {pantry.phone || pantry.email || pantry.website ? (
          <View style={styles.contactContainer}>
            {pantry.phone ? (
              <View
                style={styles.contactRow}
                accessibilityLabel={(translation?.accessibility_phone || 'Phone: {phone}').replace(
                  '{phone}',
                  pantry.phone
                )}
              >
                <Ionicons
                  name="call-outline"
                  size={14}
                  color={Colors.veryDarkGray}
                  style={styles.contactIcon}
                />
                <AppText style={styles.contactText}>{pantry.phone}</AppText>
              </View>
            ) : null}
            {pantry.email ? (
              <View
                style={styles.contactRow}
                accessibilityLabel={(translation?.accessibility_email || 'Email: {email}').replace(
                  '{email}',
                  pantry.email
                )}
              >
                <Ionicons
                  name="mail-outline"
                  size={14}
                  color={Colors.veryDarkGray}
                  style={styles.contactIcon}
                />
                <AppText style={styles.contactText}>{pantry.email}</AppText>
              </View>
            ) : null}
            {pantry.website ? (
              <View
                style={styles.contactRow}
                accessibilityLabel={(translation?.accessibility_website || 'Website: {website}').replace(
                  '{website}',
                  pantry.website
                )}
              >
                <Ionicons
                  name="globe-outline"
                  size={14}
                  color={Colors.veryDarkGray}
                  style={styles.contactIcon}
                />
                <AppText style={styles.contactText} numberOfLines={1}>
                  {pantry.website}
                </AppText>
              </View>
            ) : null}
          </View>
        ) : null}

        {pantry.distance ? (
          <AppText style={styles.distanceText}>{formatDistance(pantry.distance)}</AppText>
        ) : null}

        <AppText onPress={onPress} style={styles.link}>
          {translation?.view_pantry || 'VIEW PANTRY'}
        </AppText>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    borderTopColor: Colors.grayBorder,
    borderTopWidth: 1,
    padding: 16,
  },
  pantryDetailsTextContainer: {
    paddingLeft: 16,
    width: Dimensions.get('window').width - 112,
  },
  pantryDetailsName: {
    flexWrap: 'wrap',
    fontFamily: 'roboto-regular',
    color: Colors.veryDarkGray,
    fontSize: 16,
    fontWeight: '600',
  },
  programsLabel: {
    flexWrap: 'wrap',
    fontFamily: 'roboto-regular',
    color: Colors.veryDarkGray,
    fontSize: 12,
    fontWeight: '400',
    marginTop: 2,
  },
  addressContainer: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: Colors.lightBackground || '#F5F5F5',
    borderRadius: 4,
  },
  addressText: {
    flexWrap: 'wrap',
    fontFamily: 'roboto-regular',
    color: Colors.veryDarkGray,
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 20,
  },
  highlighted: {
    backgroundColor: '#FFF9C4', // Yellow highlight
    fontWeight: '700',
  },
  contactContainer: {
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: '#F0F8FF', // Light blue background
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: Colors.primaryColor || '#2196F3',
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 2,
  },
  contactIcon: {
    marginRight: 6,
  },
  contactText: {
    flex: 1,
    flexWrap: 'wrap',
    fontFamily: 'roboto-regular',
    color: Colors.veryDarkGray,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
  },
  distanceText: {
    fontFamily: 'roboto-regular',
    color: Colors.primaryColor,
    fontSize: 12,
    fontWeight: '600',
    marginTop: 6,
  },
  link: {
    fontFamily: 'roboto-regular',
    color: Colors.primaryColor,
    fontSize: 12,
    fontWeight: '600',
    marginTop: 8,
    textDecorationLine: 'underline',
    textTransform: 'uppercase',
  },
});

SearchResultItem.propTypes = {
  pantry: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    pantry_name: PropTypes.string,
    street: PropTypes.string,
    address_street_1: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zipcode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    avatar: PropTypes.string,
    small_photo_url: PropTypes.string,
    programs: PropTypes.array,
    distance: PropTypes.number,
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    website: PropTypes.string,
  }).isRequired,
  searchQuery: PropTypes.string.isRequired,
  onPress: PropTypes.func.isRequired,
  translation: PropTypes.object,
};

export default SearchResultItem;
